'use client'

import { useTheme } from '@/contexts/ThemeContext'
import { Handshake, Store, GraduationCap, MapPin, Mail, Phone } from 'lucide-react'

export default function PartnershipPage() {
  const { theme } = useTheme()

  const partnerTypes = [
    {
      icon: <Store className={`w-8 h-8 ${theme === 'light' ? 'text-blue-500' : 'text-blue-400'}`} />,
      title: '다이빙센터',
      description: '다이빙센터와 함께 안전한 다이빙 환경을 조성하고 고품질 서비스를 제공합니다.',
      benefits: [
        '플랫폼을 통한 고객 유입',
        '예약 시스템 연동',
        '안전 교육 프로그램 공동 개발',
        '마케팅 지원'
      ]
    },
    {
      icon: <GraduationCap className={`w-8 h-8 ${theme === 'light' ? 'text-blue-500' : 'text-blue-400'}`} />,
      title: '교육기관',
      description: '프리다이빙 교육기관과의 협력을 통해 체계적인 교육 시스템을 구축합니다.',
      benefits: [
        '교육 콘텐츠 공동 개발',
        '강사 인증 시스템 연동',
        '교육생 관리 플랫폼 제공',
        '온라인 교육 도구 지원'
      ]
    },
    {
      icon: <Handshake className={`w-8 h-8 ${theme === 'light' ? 'text-blue-500' : 'text-blue-400'}`} />,
      title: '장비업체',
      description: '다이빙 장비 업체와의 파트너십을 통해 사용자에게 최적화된 장비를 추천합니다.',
      benefits: [
        '제품 추천 시스템 연동',
        '사용자 맞춤 장비 추천',
        '할인 혜택 제공',
        '신제품 테스트 기회'
      ]
    }
  ]

  const requirements = [
    '프리다이빙 관련 사업 운영 경험',
    '안전을 최우선으로 하는 서비스 철학',
    '지속적인 협력 의지',
    '고객 서비스 품질 유지 능력'
  ]

  return (
    <div className={`min-h-screen ${theme === 'light' ? 'bg-gray-50' : 'bg-gray-900'}`}>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* 헤더 */}
        <div className="text-center mb-16 xxs:mb-8">
          <h1 className={`text-4xl xxs:text-2xl md:text-5xl font-bold mb-6 ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>
            <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
              파트너십
            </span>
          </h1>
          <p className={`text-xl xxs:text-base max-w-3xl mx-auto ${theme === 'light' ? 'text-gray-600' : 'text-gray-300'}`}>
            더 안전하고 즐거운 다이빙 문화를 함께 만들어갈 파트너를 찾고 있습니다.
            오늘다이빙과 함께 프리다이빙 생태계를 발전시켜보세요.
          </p>
        </div>

        {/* 파트너십 유형 */}
        <div className="mb-16 xxs:mb-8">
          <h2 className={`text-3xl xxs:text-xl font-bold mb-8 xxs:mb-4 text-center ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>
            파트너십 유형
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 xxs:gap-4">
            {partnerTypes.map((type, index) => (
              <div
                key={index}
                className={`p-6 xxs:p-3 rounded-xl ${theme === 'light' ? 'bg-white shadow-md' : 'bg-gray-800'}`}
              >
                <div className="flex justify-center mb-4">
                  {type.icon}
                </div>
                <h3 className={`text-xl xxs:text-base font-semibold text-center mb-4 ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>
                  {type.title}
                </h3>
                <p className={`text-center mb-6 ${theme === 'light' ? 'text-gray-600' : 'text-gray-300'}`}>
                  {type.description}
                </p>
                <div>
                  <h4 className={`font-medium mb-3 ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>
                    혜택
                  </h4>
                  <ul className="space-y-2">
                    {type.benefits.map((benefit, benefitIndex) => (
                      <li key={benefitIndex} className="flex items-start">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className={`text-sm ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'}`}>
                          {benefit}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 파트너 요구사항 */}
        <div className={`mb-16 xxs:mb-8 p-8 xxs:p-4 rounded-xl ${theme === 'light' ? 'bg-white shadow-md' : 'bg-gray-800'}`}>
          <h2 className={`text-3xl xxs:text-xl font-bold mb-6 text-center ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>
            파트너 요구사항
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {requirements.map((requirement, index) => (
              <div key={index} className="flex items-center">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                <span className={`${theme === 'light' ? 'text-gray-700' : 'text-gray-300'}`}>
                  {requirement}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* 파트너십 프로세스 */}
        <div className={`mb-16 xxs:mb-8 p-8 xxs:p-4 rounded-xl ${theme === 'light' ? 'bg-white shadow-md' : 'bg-gray-800'}`}>
          <h2 className={`text-3xl xxs:text-xl font-bold mb-8 xxs:mb-4 text-center ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>
            파트너십 프로세스
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 xxs:gap-3">
            {[
              { step: '1', title: '문의', description: '파트너십 문의서 제출' },
              { step: '2', title: '검토', description: '제출 자료 검토 및 평가' },
              { step: '3', title: '미팅', description: '온라인/오프라인 미팅' },
              { step: '4', title: '계약', description: '파트너십 계약 체결' }
            ].map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold">
                  {process.step}
                </div>
                <h3 className={`text-lg xxs:text-base font-semibold mb-2 ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>
                  {process.title}
                </h3>
                <p className={`text-sm ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'}`}>
                  {process.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* 기존 파트너 */}
        <div className={`mb-16 xxs:mb-8 p-8 xxs:p-4 rounded-xl ${theme === 'light' ? 'bg-white shadow-md' : 'bg-gray-800'}`}>
          <h2 className={`text-3xl xxs:text-xl font-bold mb-6 text-center ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>
            파트너사
          </h2>
          <p className={`text-center mb-8 ${theme === 'light' ? 'text-gray-600' : 'text-gray-300'}`}>
            현재 오늘다이빙과 함께하는 파트너들입니다.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              '제주 프리다이빙센터',
              '부산 아쿠아센터',
              '서울 다이빙스쿨',
              '경주 마린클럽'
            ].map((partner, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg text-center ${theme === 'light' ? 'bg-gray-50' : 'bg-gray-700'}`}
              >
                <div className={`h-16 ${theme === 'light' ? 'bg-gray-200' : 'bg-gray-600'} rounded mb-2 flex items-center justify-center`}>
                  <span className={`text-sm ${theme === 'light' ? 'text-gray-500' : 'text-gray-400'}`}>
                    로고
                  </span>
                </div>
                <p className={`text-sm ${theme === 'light' ? 'text-gray-700' : 'text-gray-300'}`}>
                  {partner}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* 연락처 */}
        <div className={`text-center p-8 xxs:p-4 rounded-xl ${theme === 'light' ? 'bg-blue-50' : 'bg-blue-900/20'}`}>
          <h2 className={`text-2xl xxs:text-lg font-bold mb-4 ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>
            파트너십 문의
          </h2>
          <p className={`mb-6 ${theme === 'light' ? 'text-gray-600' : 'text-gray-300'}`}>
            파트너십에 관심이 있으시다면 언제든 연락해주세요.
            자세한 제안서와 함께 검토해드리겠습니다.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            <div className="flex items-center justify-center">
              <Mail className="w-5 h-5 mr-2 text-blue-500" />
              <span className={`${theme === 'light' ? 'text-gray-700' : 'text-gray-300'}`}>
                partnership@todaydiving.com
              </span>
            </div>
            <div className="flex items-center justify-center">
              <Phone className="w-5 h-5 mr-2 text-blue-500" />
              <span className={`${theme === 'light' ? 'text-gray-700' : 'text-gray-300'}`}>
                02-1234-5678
              </span>
            </div>
          </div>
          <p className={`text-sm mt-4 ${theme === 'light' ? 'text-gray-500' : 'text-gray-400'}`}>
            * 평일 9:00-18:00 운영 (점심시간 12:00-13:00 제외)
          </p>
        </div>
      </div>
    </div>
  )
}